import React from 'react';
import SectionHeader from '@components/common/SectionHeader';
import type {
  InstructionsChartBundlePayload,
  InstructionsImpactData,
} from '@app-types/instructions';
import InstructionsImpactChartFrame from './InstructionsImpactChartFrame';
import './InstructionsImpactSection.less';

export interface InstructionsImpactSectionProps {
  data: InstructionsImpactData;
  payload: InstructionsChartBundlePayload | null;
  state: InstructionsImpactState;
  disclosureState?: InstructionsDisclosureState;
}

export type InstructionsImpactState = 'loading' | 'error' | 'empty' | 'ready';
export type InstructionsDisclosureState = 'loading' | 'error' | 'ready';

const STATE_MESSAGE: Record<Exclude<InstructionsImpactState, 'ready'>, string> = {
  loading: '株価データを読み込んでいます。',
  empty: '株価データはありません。',
  error: '株価データの取得に失敗しました。',
};

const DISCLOSURE_STATE_MESSAGE: Record<
  Exclude<InstructionsDisclosureState, 'ready'>,
  string
> = {
  loading: '適時開示情報を読み込んでいます。',
  error: '適時開示情報の取得に失敗しました。',
};

/**
 * Impact of Timely Disclosure section.
 * Panel: bordered white box containing the supplied Chart5 iframe and legend.
 * See kiro_Instructions_tasks.md section 5.
 */
const InstructionsImpactSection: React.FC<InstructionsImpactSectionProps> = ({
  data,
  payload,
  state,
  disclosureState = 'ready',
}) => {
  const renderState = state === 'ready' && !payload ? 'empty' : state;
  const stateMessage = renderState === 'ready' ? null : STATE_MESSAGE[renderState];

  return (
    <section
      className="instructions-impact"
      data-node-id={data.sectionNodeId}
    >
      <SectionHeader
        title={data.title}
        showHelp
        dataNodeId={data.headerNodeId}
        as="h2"
      />
      <div
        className="instructions-impact__panel"
        data-node-id={data.panelNodeId}
      >
        {renderState === 'ready' && payload ? (
          <InstructionsImpactChartFrame
            payload={payload}
            className="instructions-impact__chart"
          />
        ) : (
          <p
            className="instructions-impact__state"
            role={renderState === 'error' ? 'alert' : 'status'}
            aria-busy={renderState === 'loading' || undefined}
          >
            {stateMessage}
          </p>
        )}
        {renderState === 'ready' && disclosureState !== 'ready' && (
          <p
            className="instructions-impact__disclosure-state"
            role={disclosureState === 'error' ? 'alert' : 'status'}
            aria-busy={disclosureState === 'loading' || undefined}
          >
            {DISCLOSURE_STATE_MESSAGE[disclosureState]}
          </p>
        )}
        <ul className="instructions-impact__legend">
          {data.legend.map((item) => (
            <li key={item.key} className="instructions-impact__legend-item">
              {item.type === 'line' ? (
                <span
                  className="instructions-impact__legend-line"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                />
              ) : (
                <span
                  className="instructions-impact__legend-event"
                  style={{ backgroundColor: item.color }}
                  aria-hidden="true"
                >
                  {item.markerLabel}
                </span>
              )}
              <span className="instructions-impact__legend-label">{item.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default InstructionsImpactSection;
